/** Saved-board form and constrained styling help. Rust validates every draft; this component only edits fields and renders errors.
 * @module apps/web/src/components/kanban/KanbanViewEditor
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { TaskUI, WorkspaceBundleUI } from '@fluxability/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { projectKanban, type KanbanCardField, type KanbanSpec } from '@/lib/kanbanApi';

/** Editor inputs; key the component by selected revision to preserve stale-save detection. */
interface Props {
  /** Configuration copied into this form once. */
  initial: KanbanSpec;
  /** Built-in configuration used for an explicit reset. */
  defaults: KanbanSpec;
  /** Current tasks used only to discover existing groups through Rust. */
  tasks: TaskUI[];
  /** Owner-visible bundle labels. */
  bundles: WorkspaceBundleUI[];
  /** Whether persistence is in progress. */
  busy: boolean;
  /** Persistence error, including stale revision conflicts. */
  error: string | null;
  /** Save the complete draft without replacing the captured revision. */
  onSave: (spec: KanbanSpec) => void;
  /** Dismiss without saving. */
  onClose: () => void;
}
/** UI options only; policy and accepted values remain in the shared Rust validator. */
const cardFields: KanbanCardField[] = ['duration', 'deadline', 'priority', 'bundle'];
/** Plain input styling retained outside all user paint surfaces. */
const inputClass = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm';
/** Full saved-view form; errors disable save rather than silently dropping unsupported CSS. */
export function KanbanViewEditor({
  initial,
  defaults,
  tasks,
  bundles,
  busy,
  error,
  onSave,
  onClose,
}: Props) {
  const [draft, setDraft] = useState(initial);
  const checked = useMemo(() => projectKanban(draft, [], []), [draft]);
  const feedback = useRef<HTMLDivElement>(null);
  // Reveal a newly rejected draft once without moving focus or scrolling on each invalid keystroke.
  useLayoutEffect(() => {
    if (!checked.ok || error)
      feedback.current?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }, [checked.ok, error]);
  const grouping = draft.grouping;
  const available = useMemo(
    () => projectKanban({ ...defaults, grouping }, tasks, bundles),
    [defaults, grouping, tasks, bundles]
  );
  const changeColumn = (id: string, patch: Partial<KanbanSpec['columns'][number]>) =>
    setDraft(current => {
      const existing = current.columns.find(column => column.id === id);
      const next = { id, label: null, order: null, visible: true, ...existing, ...patch };
      return { ...current, columns: [...current.columns.filter(column => column.id !== id), next] };
    });
  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customize Kanban view</DialogTitle>
          <DialogDescription>
            Saved presentation changes keep your activities and their status intact.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={event => {
            event.preventDefault();
            if (checked.ok && !busy) onSave(draft);
          }}
          className="space-y-4"
        >
          <label className="block space-y-1">
            View name
            <input
              aria-label="View name"
              className={inputClass}
              value={draft.name}
              maxLength={80}
              onChange={event => setDraft({ ...draft, name: event.target.value })}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <label>
              Group by
              <select
                aria-label="Group by"
                className={inputClass}
                value={draft.grouping}
                onChange={event =>
                  setDraft({
                    ...draft,
                    grouping: event.target.value as KanbanSpec['grouping'],
                    columns: [],
                  })
                }
              >
                <option value="status">Status</option>
                <option value="priority">Priority</option>
                <option value="bundle">Bundle</option>
              </select>
            </label>
            <label>
              Sort cards
              <select
                aria-label="Sort cards"
                className={inputClass}
                value={draft.sort}
                onChange={event =>
                  setDraft({ ...draft, sort: event.target.value as KanbanSpec['sort'] })
                }
              >
                <option value="manual">Existing order</option>
                <option value="title">Title</option>
                <option value="due_date">Due date</option>
                <option value="priority">Priority</option>
              </select>
            </label>
            <label>
              Card density
              <select
                aria-label="Card density"
                className={inputClass}
                value={draft.density}
                onChange={event =>
                  setDraft({ ...draft, density: event.target.value as KanbanSpec['density'] })
                }
              >
                <option value="comfortable">Comfortable</option>
                <option value="compact">Compact</option>
              </select>
            </label>
          </div>
          <fieldset>
            <legend className="font-medium">Card details</legend>
            <div className="flex flex-wrap gap-4">
              {cardFields.map(field => (
                <label key={field} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.cardFields.includes(field)}
                    onChange={event =>
                      setDraft({
                        ...draft,
                        cardFields: event.target.checked
                          ? [...draft.cardFields, field]
                          : draft.cardFields.filter(item => item !== field),
                      })
                    }
                  />
                  {field}
                </label>
              ))}
            </div>
            <p className="text-muted-foreground text-xs">
              Titles, editing, progress, and status controls always remain available.
            </p>
          </fieldset>
          <fieldset>
            <legend className="font-medium">Filters</legend>
            <div className="grid gap-3 sm:grid-cols-3">
              <label>
                Status filter
                <select
                  aria-label="Status filter"
                  className={inputClass}
                  value={draft.filters.statuses[0] ?? ''}
                  onChange={event =>
                    setDraft({
                      ...draft,
                      filters: {
                        ...draft.filters,
                        statuses: event.target.value ? [event.target.value] : [],
                      },
                    })
                  }
                >
                  <option value="">All statuses</option>
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="done">Done</option>
                </select>
              </label>
              <label>
                Priority filter
                <select
                  aria-label="Priority filter"
                  className={inputClass}
                  value={draft.filters.priorities[0] ?? ''}
                  onChange={event =>
                    setDraft({
                      ...draft,
                      filters: {
                        ...draft.filters,
                        priorities: event.target.value ? [Number(event.target.value)] : [],
                      },
                    })
                  }
                >
                  <option value="">All priorities</option>
                  {[1, 2, 3, 4].map(priority => (
                    <option key={priority} value={priority}>
                      P{priority}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Bundle filter
                <select
                  aria-label="Bundle filter"
                  className={inputClass}
                  value={draft.filters.bundleIds[0] ?? ''}
                  onChange={event =>
                    setDraft({
                      ...draft,
                      filters: {
                        ...draft.filters,
                        bundleIds: event.target.value ? [event.target.value] : [],
                      },
                    })
                  }
                >
                  <option value="">All bundles</option>
                  <option value="bundle:unassigned">Unassigned</option>
                  {bundles.map(bundle => (
                    <option key={bundle.id} value={`bundle:id:${bundle.id}`}>
                      {bundle.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </fieldset>
          <fieldset>
            <legend className="font-medium">Columns</legend>
            <p className="text-muted-foreground mb-2 text-xs">
              Lower order numbers appear first. Hiding a column never deletes its activities.
            </p>
            <div className="space-y-2">
              {available.ok &&
                available.projection.columns.map(column => {
                  const setting = draft.columns.find(item => item.id === column.id);
                  return (
                    <div key={column.id} className="grid grid-cols-[1fr_4rem_4rem] items-end gap-2">
                      <label className="text-sm">
                        {column.title}
                        <input
                          aria-label={`${column.title} column label`}
                          className={inputClass}
                          value={setting?.label ?? ''}
                          placeholder={column.title}
                          maxLength={60}
                          onChange={event =>
                            changeColumn(column.id, { label: event.target.value || null })
                          }
                        />
                      </label>
                      <label className="text-sm">
                        Order
                        <input
                          aria-label={`${column.title} column order`}
                          type="number"
                          min={0}
                          max={31}
                          className={inputClass}
                          value={setting?.order ?? ''}
                          onChange={event =>
                            changeColumn(column.id, {
                              order: event.target.value === '' ? null : Number(event.target.value),
                            })
                          }
                        />
                      </label>
                      <label className="flex h-9 items-center gap-1 text-sm">
                        <input
                          aria-label={`Show ${column.title} column`}
                          type="checkbox"
                          checked={setting?.visible ?? true}
                          onChange={event =>
                            changeColumn(column.id, { visible: event.target.checked })
                          }
                        />
                        Show
                      </label>
                    </div>
                  );
                })}
            </div>
          </fieldset>
          <label className="block space-y-1">
            View CSS
            <textarea
              aria-label="View CSS"
              rows={5}
              maxLength={4096}
              className={`${inputClass} font-mono`}
              spellCheck={false}
              value={draft.css}
              placeholder=".card { background-color: #dbeafe; border-radius: 12px; padding: 8px; }"
              onChange={event => setDraft({ ...draft, css: event.target.value })}
            />
          </label>
          <details className="rounded border p-3 text-sm">
            <summary>Supported CSS syntax</summary>
            <p className="mt-2">
              Use one rule per target: <code>.board</code> supports background-color, padding, gap;{' '}
              <code>.column</code> and <code>.card</code> support background-color, border-color,
              border-radius, padding; <code>.column-title</code> supports font-size and font-weight.
            </p>
            <p>
              Colors use #rrggbb. Padding and gap: 4–24px; radius: 0–24px; title size: 12–24px;
              weight: 400, 500, 600, 700. No comments, escapes, imports, URLs, functions, other
              selectors, or control styling.
            </p>
          </details>
          <div ref={feedback} className="space-y-2">
            {!checked.ok && (
              <p role="alert" className="text-destructive text-sm">
                {checked.error}
              </p>
            )}
            {error && (
              <p role="alert" className="text-destructive text-sm">
                {error}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={!checked.ok || busy}>
              {busy ? 'Saving…' : 'Save view'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setDraft({ ...defaults, name: draft.name })}
            >
              Reset settings
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
